import { RefObject, useCallback, useEffect, useRef } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

interface IUseFullscreenOnMobileParams {
    elementRef: RefObject<HTMLElement | null>;
    isActive: boolean;
    onExitFullscreen?: () => void;
}

export const useFullscreenOnMobile = ({ elementRef, isActive, onExitFullscreen }: IUseFullscreenOnMobileParams) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isFullscreenActive = useRef(false);

    const handleFullscreenChange = useCallback(() => {
        if (document.fullscreenElement) {
            isFullscreenActive.current = true;
        } else if (isFullscreenActive.current) {
            isFullscreenActive.current = false;
            onExitFullscreen?.();
        }
    }, [onExitFullscreen]);

    useEffect(() => {
        if (!isActive || !isMobile || !document.fullscreenEnabled) {
            return;
        }

        // Fullscreen request denied — keep the modal open without fullscreen
        elementRef.current?.requestFullscreen().catch(() => {});
        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);

            if (isFullscreenActive.current) {
                isFullscreenActive.current = false;
                document.exitFullscreen();
            }
        };
    }, [isActive, isMobile, elementRef, handleFullscreenChange]);
};
